/**
 * FindIdPage — A-2-2. 아이디 찾기
 * 이름 + 전화번호 → POST /member/find-id
 * 404 시 GET /member/users/sns-check → SNS 다이얼로그 or 인라인 에러
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Alert, Button, Input } from '@indipia/ui-core';
import { AuthLayout } from '../../app/layouts/AuthLayout';
import { SnsAccountDialog } from '../components/SnsAccountDialog';
import { findId, checkSns } from '../api/member-auth';

export function FindIdPage() {
  const navigate = useNavigate();

  const [name,      setName]      = useState('');
  const [mobile,    setMobile]    = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result,    setResult]    = useState<{ loginId: string; createdAt: string } | null>(null);
  const [error,     setError]     = useState<string | null>(null);
  const [snsDialog, setSnsDialog] = useState<{ types: string[] } | null>(null);

  const formatPhone = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length < 4) return d;
    if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  };

  const canSubmit = name.trim().length > 0 && mobile.replace(/\D/g, '').length >= 10;

  const handleSubmit = async () => {
    if (!canSubmit || isLoading) return;
    setIsLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await findId({ memberName: name.trim(), mobile });
      setResult(data);
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        try {
          const sns = await checkSns({ mobile });
          if (sns.snsYn === 'Y') {
            setSnsDialog({ types: sns.snsTypes });
          } else {
            setError('일치하는 계정이 없어요.');
          }
        } catch {
          setError('일치하는 계정이 없어요.');
        }
      } else {
        setError('조회 중 오류가 발생했습니다. 다시 시도해 주세요.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} 가입`;
  };

  return (
    <>
      <AuthLayout
        kicker="Find Account ID"
        title="아이디 찾기"
        subtitle={<>가입 시 등록한 정보로<br />아이디를 찾아드려요.</>}
        actions={
          <div className="flex flex-col gap-sm">
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              disabled={!canSubmit}
              loading={isLoading}
              onClick={() => void handleSubmit()}
            >
              아이디 찾기
            </Button>
            {/* 보조 강조 액션은 secondary(아웃라인) — D-09 §15.1 Variants */}
            <Button variant="secondary" size="lg" className="w-full" onClick={() => navigate('/login')}>
              로그인으로 돌아가기
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-md">
          <Input
            label="이름"
            width="fill"
            size="lg"
            type="text"
            placeholder="가입 시 등록한 이름"
            value={name}
            onChange={(e) => { setName(e.target.value); setError(null); setResult(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') void handleSubmit(); }}
          />

          <Input
            label="전화번호"
            width="fill"
            size="lg"
            type="tel"
            placeholder="010-0000-0000"
            value={mobile}
            onChange={(e) => { setMobile(formatPhone(e.target.value)); setError(null); setResult(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') void handleSubmit(); }}
          />

          {error && <Alert kind="error">{error}</Alert>}

          {result && (
            <div
              className="px-05 py-05"
              style={{
                background: 'var(--shell-point-subtle)',
                border: '1px solid var(--shell-point)',
                borderRadius: 'var(--radius-lg)',
              }}
            >
              <p className="mb-02 text-caption text-text-secondary">{formatDate(result.createdAt)}</p>
              <p className="text-heading-md font-bold text-text">{result.loginId}</p>
            </div>
          )}
        </div>
      </AuthLayout>

      <SnsAccountDialog
        open={snsDialog != null}
        types={snsDialog?.types ?? []}
        onClose={() => setSnsDialog(null)}
        onGoLogin={() => { setSnsDialog(null); navigate('/login'); }}
      />
    </>
  );
}
